import * as Context from "effect-app/Context"
import * as Effect from "effect-app/Effect"
import { type RouteLocationAsPath, type RouteLocationAsRelative, type RouteLocationAsRelativeTyped, type RouteLocationAsString, type RouteLocationNormalizedLoaded, type RouteLocationRaw, type RouteLocationResolved, type RouteMap, type RouteRecordNameGeneric, type RouteRecordRaw, useRoute, useRouter } from "vue-router"

/**
 * Effectified version of `useRouter`
 */
export const useEffectRouter = () => {
  // router is not always available on the frontend; tolerate its absence
  const r = useRouter()
  const effectified = {
    ...r,
    current: useRoute(),
    back: Effect.sync(() => r.back()),
    forward: Effect.sync(() => r.forward()),
    replace: (to: RouteLocationRaw) => Effect.promise(() => r.replace(to)),
    push: (to: RouteLocationRaw) => Effect.promise(() => r.push(to)),
    isReady: Effect.promise(() => r.isReady())
  }
  return effectified
}

export class Router extends Context.Opaque<Router>()("Router", {
  make: Effect.sync(useEffectRouter)
}) {
  static readonly Default = Router.toLayer(Effect.sync(useEffectRouter))

  static addRoute(
    parentName: NonNullable<RouteRecordNameGeneric>,
    route: RouteRecordRaw
  ): Effect.Effect<void, never, Router>
  static addRoute(route: RouteRecordRaw): Effect.Effect<void, never, Router>
  static addRoute(
    ...args:
      | [RouteRecordRaw]
      | [NonNullable<RouteRecordNameGeneric>, RouteRecordRaw]
  ): Effect.Effect<void, never, Router> {
    return Router.useSync((_) => {
      if (args.length === 2) _.addRoute(args[0], args[1])
      else _.addRoute(args[0])
    })
  }

  static push(to: RouteLocationRaw): Effect.Effect<void, never, Router> {
    return Router.use((_) => _.push(to))
  }

  static replace(to: RouteLocationRaw): Effect.Effect<void, never, Router> {
    return Router.use((_) => _.replace(to))
  }

  static readonly back: Effect.Effect<void, never, Router> = Router.use(
    (_) => _.back
  )

  static readonly forward: Effect.Effect<void, never, Router> = Router.use(
    (_) => _.forward
  )

  static resolve<Name extends keyof RouteMap = keyof RouteMap>(
    to: RouteLocationAsRelativeTyped<RouteMap, Name>,
    currentLocation?: RouteLocationNormalizedLoaded
  ): Effect.Effect<RouteLocationResolved<Name>, never, Router>
  static resolve(
    to: RouteLocationAsString | RouteLocationAsRelative | RouteLocationAsPath,
    currentLocation?: RouteLocationNormalizedLoaded
  ): Effect.Effect<RouteLocationResolved, never, Router>
  static resolve(
    to:
      | RouteLocationAsRelativeTyped<RouteMap>
      | RouteLocationAsString
      | RouteLocationAsRelative
      | RouteLocationAsPath,
    currentLocation?: RouteLocationNormalizedLoaded
  ): Effect.Effect<RouteLocationResolved, never, Router> {
    return Router.useSync((_) => _.resolve(to, currentLocation))
  }
}
